/**
 * @fileoverview FactStrip: a fact that is also a door (B136, design.md rev 12). A tinted rounded-square strip, 40px tall, on the
 * tiles' soft tint (bg-muted/70, 12px corners), with a leading icon, the words and a bold chevron at the right end; a tap opens a
 * tray right under it. It is one control for the plant card's two strips: the planting line ("Next: sow indoors late March to
 * April") and the Care line ("Care · likes 65-85°F · water about weekly"), so both read as buttons at a glance.
 *
 * The `now` look is for a planting window that is open (the server's canPlantNow): the warm accent tint with the accent ink,
 * bold. The chevron turns when the strip is open and the strip says so to a screen reader (aria-expanded). The component draws
 * the words it is given; it decides nothing.
 *
 * Where: PlantingDatesLine (the planting strip) and CareStrip (the Care strip), both on the plant card.
 * API: none.
 * Words: none; the caller gives them.
 *
 * @module FactStrip
 */

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** What a strip is given: its icon, state and words, and any button attribute. */
export interface FactStripProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** The leading icon, as an element (sized by the strip). */
  icon: React.ReactNode;
  /** Whether its tray is open (turns the chevron; sets aria-expanded). */
  open: boolean;
  /** The "plant now" look: the warm accent tint, accent ink, bold. */
  now?: boolean;
  /** Classes from the caller, for its place on the card. */
  className?: string;
  /** The strip's words (a span, or a few). */
  children: React.ReactNode;
}

/**
 * The strip: icon, words, chevron.
 *
 * @param props - FactStripProps.
 */
export const FactStrip = React.forwardRef<HTMLButtonElement, FactStripProps>(
  ({ icon, open, now = false, className, children, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-expanded={open}
      className={cn(
        'flex h-10 w-full min-w-0 items-center gap-2 rounded-xl py-0 pl-3 pr-2.5 text-left text-sm leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        now ? 'bg-accent font-bold text-accent-ink' : 'bg-muted/70 font-semibold text-foreground hover:bg-muted',
        className,
      )}
      {...rest}
    >
      <span className={cn('inline-flex shrink-0 items-center [&_svg]:h-[1.125rem] [&_svg]:w-[1.125rem]', now ? 'text-accent-ink' : 'text-primary')} aria-hidden="true">{icon}</span>
      <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">{children}</span>
      <ChevronDown className={cn('ml-auto h-[1.375rem] w-[1.375rem] shrink-0 transition-transform', open && 'rotate-180')} strokeWidth={2.4} aria-hidden="true" />
    </button>
  ),
);
FactStrip.displayName = 'FactStrip';
